import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  BackLink,
  Button,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Pill,
  SegmentedControl,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { contractPaths, subjectFromParams } from '@/lib/contracts';
import { readContract, readContractDocument, readContractPreviewPages } from '@/lib/contracts';

/** Preview surfaces: the provider's rendered PDF, or our paginated document. */
const MODES = [
  { key: 'rendered', label: 'As DocuSeal renders it' },
  { key: 'html', label: 'Document' },
] as const;

type Mode = (typeof MODES)[number]['key'];

/**
 * Paginates the document HTML into Letter pages with paged.js — estimating
 * where content breaks and honouring our explicit `[data-page-break]`
 * markers — so the Document view previews real pages, not a continuous flow.
 *
 * @param html - The full document HTML.
 * @returns HTML wired for in-iframe pagination.
 */
function paginate(html: string): string {
  const pageCss = `
    <style>
      @page { size: 8.5in 11in; margin: 0.6in; }
      html, body { background: #e9edf2; margin: 0; }
      .pagedjs_page {
        background: #fff;
        margin: 0 auto 18px;
        box-shadow: 0 1px 8px rgba(0,0,0,0.15);
      }
    </style>`;
  return html
    .replace('</head>', `${pageCss}</head>`)
    .replace('</body>', `<script src="/paged.polyfill.js"></script></body>`);
}

/**
 * Full-page read-only preview of a contract. "As DocuSeal renders it" shows
 * the provider's actual rendered PDF (what the signer sees); "Document"
 * shows our HTML paginated into Letter pages.
 *
 * @returns The preview page.
 */
export function ContractPreviewPage(): React.ReactElement {
  const { eventId, campaignId, requestId, organizationId, contractId } = useParams<{ eventId?: string; campaignId?: string; requestId?: string; organizationId?: string; contractId: string }>();
  const paths = contractPaths(subjectFromParams({ eventId, campaignId, requestId, organizationId }));
  const [mode, setMode] = useState<Mode>('rendered');

  const contract = useQuery({
    queryKey: ['contract', contractId],
    queryFn: () => readContract(contractId ?? ''),
    enabled: contractId !== undefined,
  });

  const pdf = useQuery({
    queryKey: ['contract-preview-pdf', contractId],
    queryFn: () => readContractPreviewPages(contractId ?? ''),
    enabled: contractId !== undefined && mode === 'rendered',
  });

  const document = useQuery({
    queryKey: ['contract-document', contractId],
    queryFn: () => readContractDocument(contractId ?? ''),
    enabled: contractId !== undefined && mode === 'html',
  });

  const paginated = useMemo(
    () => (document.data ? paginate(document.data.html) : null),
    [document.data],
  );

  return (
    <section className="animate-portal-rise">
      <BackLink to={paths.back}>Back to contracts</BackLink>

      <div className="mt-3">
        <PageHeading
          eyebrow="CONTRACT PREVIEW"
          size="compact"
          title={contract.data?.name ?? 'Contract'}
          action={
            <div className="flex items-center gap-2">
              {contract.data ? <Pill tone="neutral">Revision {contract.data.revision}</Pill> : null}
              <Button
                size="compact"
                variant="secondary"
                to={paths.page(contractId ?? '', 'text')}
              >
                Edit
              </Button>
            </div>
          }
        />
      </div>

      <div className="mt-4">
        <SegmentedControl<Mode>
          label="Preview mode"
          options={MODES.map((m) => ({ key: m.key, label: m.label }))}
          value={mode}
          onChange={setMode}
        />
      </div>

      <div className="mt-4">
        {mode === 'rendered' ? (
          <>
            {pdf.isPending ? <LoadingState label="Rendering document…" /> : null}
            {pdf.isError ? (
              <ErrorNotice title="Preview unavailable" message={errorMessage(pdf.error)} />
            ) : null}
            {pdf.data && !pdf.data.url ? (
              <ErrorNotice
                title="No rendered document yet"
                message="The provider hasn't produced a rendered PDF for this template. Try the Document view."
              />
            ) : null}
            {pdf.data?.url ? (
              <iframe
                title="Contract PDF preview"
                src={pdf.data.url}
                className="block h-[82vh] w-full rounded-portal-button border bg-white"
              />
            ) : null}
          </>
        ) : (
          <>
            {document.isPending ? <LoadingState label="Loading document…" /> : null}
            {document.isError ? (
              <ErrorNotice title="Preview unavailable" message={errorMessage(document.error)} />
            ) : null}
            {paginated ? (
              <iframe
                title="Contract document preview"
                srcDoc={paginated}
                sandbox="allow-scripts allow-same-origin"
                className="block h-[82vh] w-full rounded-portal-button border"
              />
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
